import { Formik } from "formik";
import React, { useContext, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { Button,  ThemeProviderV2} from '@blueprint/ui';
import { useModal } from "react-modal-hook";
import { useSelector } from "react-redux";
import * as yup from "yup";

import claimApi from "../../../api/claim";
import { Loader, toast } from "../../../common/components/core";
import { TextareaField } from "../../../common/components/form";
import { useAuthErrorHandlerCreator } from "../../../common/hooks";
import { currentRolesSelector } from "../../../redux/slices/auth/selectors";
import { FormSubmissionHandler, Handler } from "../../../types/common";
import {
    composeErrorHandlers,
    GenericError,
    onStaleStateError,
} from "../../../utils/errorHandling";
import { isUserClaimApprover } from "../../../utils/role";
import ClaimContext from "../ClaimContext";

export type RejectClaimFormValues = {
    comment: string;
};

const schema: yup.SchemaOf<RejectClaimFormValues> = yup.object().shape({
    comment: yup.string().default(""),
});

const useRejectClaimHandler = (claimRef: string): Handler => {
    const [isSubmitting, setSubmitting] = useState(false);
    const onAuthError = useAuthErrorHandlerCreator();
    const { mutate } = useContext(ClaimContext);
    const currentRoles = useSelector(currentRolesSelector);

    const [show, hide] = useModal(() => {
        const submit: FormSubmissionHandler<RejectClaimFormValues> = async (
            values,
        ) => {
            setSubmitting(true);
            try {
                const targetApi = isUserClaimApprover(currentRoles)
                    ? claimApi.claimApproverReject
                    : claimApi.poNegotiate;
                const result = await targetApi(claimRef, values);
                await mutate();
                toast("Claim submission rejected");
                hide();
            } catch (e) {
                composeErrorHandlers(
                    onStaleStateError(() => {
                        mutate();
                        hide();
                        toast("Page has been updated by another user.");
                    }),
                    onAuthError,
                )(e as GenericError);
            }
            setSubmitting(false);
        };

        return (
            <ThemeProviderV2>
            <Modal show onHide={hide} backdrop="static" centered>
                <Formik
                    validationSchema={schema}
                    validateOnBlur={false}
                    validateOnChange={false}
                    onSubmit={submit}
                    initialValues={{
                        comment: "",
                    }}
                >
                    {({ handleSubmit }) => (
                        <Loader overlay isLoading={isSubmitting}>
                            <Form
                                autoComplete="off"
                                noValidate
                                onSubmit={handleSubmit}
                            >
                                <Modal.Header>
                                    <h2 className="mb-0">
                                        Reject Claim
                                    </h2>
                                </Modal.Header>
                                <Modal.Body>
                                    <p>
                                        Are you sure you want to reject this
                                        Claim?
                                    </p>
                                    <TextareaField
                                        name="comment"
                                        label="Comment (Optional)"
                                        maxLength={256}
                                        isAllowed={(formattedValue) => formattedValue.length < 256}
                                    />
                                </Modal.Body>

                                <Modal.Footer className="d-flex justify-content-between px-3">
                                    <Button
                                        variant="light.tertiary"
                                        onClick={hide}
                                    >
                                        Cancel
                                    </Button>
                                    <Button variant="light.primary" type="submit">
                                        Confirm
                                    </Button>
                                </Modal.Footer>
                            </Form>
                        </Loader>
                    )}
                </Formik>
            </Modal>
            </ThemeProviderV2>
        );
    }, [claimRef, isSubmitting, mutate, onAuthError]);

    return {
        isSubmitting,
        handler: show,
    };
};

export default useRejectClaimHandler;
